import React from "react";
import { Link } from "react-router-dom";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import AppTableDateTime from "components/Shared/AppTableDateTime";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyListShipmentMessagesQuery } from "store/services/smsLogListApi";
import SmsLogListFilter from "./SmsLogListFilter";



const SmsLogListScreen = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const cardStyles = {
    currentMonth: {
      backgroundColor: isDark ? "rgba(21, 101, 192, 0.16)" : "#F5F8FF",
      borderColor: isDark ? "rgba(21, 101, 192, 0.5)" : "#BBD3F7",
      valueColor: "#1565C0",
    },
    lastMonth: {
      backgroundColor: isDark ? "rgba(46, 125, 50, 0.16)" : "#F1F8F2",
      borderColor: isDark ? "rgba(46, 125, 50, 0.5)" : "#C8E6C9",
      valueColor: "#2E7D32",
    },
  };

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { params } = useCustomParams();

  const keyword = params.search || "";
  const fromDate = params.fromDate || "";
  const toDate = params.toDate || "";
  const messageType = params.messageType || "";

  const breadcrumbs = [
    { title: "Report", path: "/app/crm/reports/list" },
    { title: "SMS Logs" },
  ];

  const columns = [
    {
      name: "Source Model",
      selector: (row) => row.sourceModel,
      cell: (row) => (
        <span className="font-medium">{row.sourceModel || "N/A"}</span>
      ),
    },
    {
      name: "Source ID",
      selector: (row) => row.sourceModel,
      cell: (row) => {
        let displayId = "N/A";
        let detailUrl = "#";

        if (row.sourceModel === "Shipment") {
          displayId = row.shipmentId || "N/A";
          detailUrl = `/app/crm/shipment/${row.shipmentObjectId}/detail?tab=sms-log`;
        }

        if (row.sourceModel === "CODTransfer") {
          displayId = row.codTransferId || "N/A";
          detailUrl = `/app/crm/cod-transfer/${row.codTransferObjectId}/detail`;
        }

        return (
          <Link
            title={`${row.sourceModel}: ${displayId}`}
            to={detailUrl}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {displayId !== "N/A" ? (
              <HighlightedText text={displayId} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        );
      },
    },
    {
      name: "Message Type",
      selector: (row) => row.messageType,
      cell: (row) => row.messageType || "N/A",
    },
    {
      name: "Message",
      selector: (row) => row.message,
      minWidth: "500px",
      wrap: true,
      cell: (row) => (
        <span className="line-clamp-2">
          {row.message ? (
            <HighlightedText text={row.message} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </span>
      ),
    },
    {
      name: "Date",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => {
        let detailUrl = "#";

        if (row.sourceModel === "Shipment") {
          detailUrl = `/app/crm/shipment/${row?.shipmentObjectId}/detail?tab=sms-log`;
        }

        if (row.sourceModel === "CODTransfer") {
          detailUrl = `/app/crm/cod-transfer/${row?.codTransferObjectId}/detail`;
        }

        return (
          <Link to={detailUrl}>
            <IconButton size="small" aria-label="View Messages" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchShipmentMessages,
    { data: shipmentMessagesRes, isLoading, isFetching, error },
  ] = useLazyListShipmentMessagesQuery();

  React.useEffect(() => {
    fetchShipmentMessages({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(messageType && { messageType }),
    });
  }, [
    currentPage,
    fetchShipmentMessages,
    keyword,
    fromDate,
    toDate,
    messageType,
    perPage,
  ]);

  useAppMeta("SMS Logs");

  const currentMonthTotal = shipmentMessagesRes?.summary?.currentMonthTotal ?? 0;
  const lastMonthTotal = shipmentMessagesRes?.summary?.lastMonthTotal ?? 0;

  const typeCounts = shipmentMessagesRes?.summary?.messageTypeTotals ?? {};

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <Box sx={{ px: 2, pt: 2 }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ flexWrap: "wrap", gap: 2 }}
          >
            {/* First Card: Month Totals stacked vertically */}
            <Box
              sx={{
                flex: "1 1 220px",
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: cardStyles.currentMonth.borderColor,
                backgroundColor: cardStyles.currentMonth.backgroundColor,
              }}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current Month
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: cardStyles.currentMonth.valueColor }}>
                    {currentMonthTotal.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Month
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: cardStyles.lastMonth.valueColor }}>
                    {lastMonthTotal.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Second Card: Message Type Breakdown stacked vertically */}
            <Box
              sx={{
                flex: "1 1 280px",
                p: 2,
                borderRadius: 2,
                border: "1px solid #E0E0E0",
                backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#FAFAFA",
              }}
            >
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Message Types
                </Typography>
                {Object.entries(typeCounts).map(([type, count]) => (
                  <Box key={type} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2">{type}</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {count.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>

        <SmsLogListFilter disabled={isLoading || isFetching} />

        <AppTable
          columns={columns}
          data={shipmentMessagesRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentMessagesRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default SmsLogListScreen;